interface FingerprintData {
  userAgent: string;
  platform: string;
  language: string;
  timezone: string;
  screenResolution: string;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  cookieEnabled: boolean;
  doNotTrack?: string | null;
  maxTouchPoints?: number;
  canvasHash?: string;
  webglHash?: string;
  ipAddress?: string;
  timestamp?: number;
}

class DeviceFingerprint {
  private fingerprintData: FingerprintData;
  private fingerprintHash: string | null = null;

  constructor() {
    this.fingerprintData = this.initializeFingerprintData();
  }

  private initializeFingerprintData(): FingerprintData {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as any).deviceMemory,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      maxTouchPoints: navigator.maxTouchPoints,
      ipAddress: undefined,
    };
  }

  private async _generateCanvasFingerprint(): Promise<void> {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillText("Device fingerprint test", 2, 2);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillRect(100, 5, 80, 20);
      this.fingerprintData.canvasHash = this._simpleHash(canvas.toDataURL());
    }
  }

  private async _generateWebGLFingerprint(): Promise<void> {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      const glContext = gl as WebGLRenderingContext;
      const debugInfo = glContext.getExtension("WEBGL_debug_renderer_info");
      this.fingerprintData.webglHash = this._simpleHash(
        debugInfo
          ? `${glContext.getParameter(
              debugInfo.UNMASKED_VENDOR_WEBGL
            )}-${glContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)}`
          : `${glContext.getParameter(
              glContext.VENDOR
            )}-${glContext.getParameter(glContext.RENDERER)}`
      );
    }
  }

  private _generateBasicFingerprint(): string {
    const basicData = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timestamp: Date.now(),
    };
    return this._simpleHash(JSON.stringify(basicData)).slice(0, 16);
  }

  private async _generateHash(): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(this.fingerprintData));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  public async generateFingerprint(): Promise<
    FingerprintData & { fingerprintHash: string }
  > {
    try {
      const ipResponse = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipResponse.json();
      this.fingerprintData.ipAddress = ipData.ip;

      await this._generateCanvasFingerprint();
      await this._generateWebGLFingerprint();

      this.fingerprintHash = await this._generateHash();
      localStorage.setItem("deviceFingerprint", this.fingerprintHash);

      return {
        ...this.fingerprintData,
        fingerprintHash: this.fingerprintHash,
      };
    } catch (error) {
      console.error("Failed to generate device fingerprint:", error);
      const basicFingerprint = this._generateBasicFingerprint();
      return {
        ...this.fingerprintData,
        fingerprintHash: basicFingerprint,
      };
    }
  }

  public async generateUniqueCatalogFingerprint(): Promise<string> {
    try {
      this.fingerprintData.timestamp = Date.now();
      const uniqueHash = await this._generateHash();
      return uniqueHash;
    } catch (error) {
      console.error("Failed to generate unique catalog fingerprint:", error);
      const basicFingerprint = this._generateBasicFingerprint();
      return basicFingerprint;
    }
  }

  private _simpleHash(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 15) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  public getFingerprintHash(): string | null {
    return this.fingerprintHash;
  }
}

export const initializeDeviceFingerprint = async (): Promise<
  FingerprintData & { fingerprintHash: string }
> => {
  const fingerprint = new DeviceFingerprint();
  return await fingerprint.generateFingerprint();
};

export default DeviceFingerprint;
